import type { MechanicDefinition } from "./types";

export const gamePhases: MechanicDefinition = {
  id: "game_phases",
  disabled: true,
  name: "Game Phases",
  description: "Controls setup flow, opening ceremony, and session structure",
  icon: "Workflow",
  category: "core",
  auditPasses: 1, // ⭐ (reviewed alongside turn_structure)
  aiHints: {
    purpose: "Controls session structure — setup flow, character creation, opening narration, and phase transitions.",
    whenToUse: "Games with distinct phases (character creation → exploration → combat → rest cycle).",
    synergies: ["turn_structure"],
    conflicts: [],
    prerequisites: [],
    tags: ["phases", "session", "structure", "setup", "core"],
    complexity: 1,
    exampleConfig: { opening: "dramatic", allow_skip: false },
    stateVariables: ["_current_phase"],
  },
  configSchema: [
    {
      key: "setup_flow",
      label: "Setup Flow",
      type: "select",
      options: [
        { value: "scenario_first", label: "Scenario → Character" },
        { value: "character_first", label: "Character → Scenario" },
        { value: "skip_setup", label: "Skip (jump into play)" },
      ],
      default: "scenario_first",
      description: "Order of pre-game setup steps",
    },
    {
      key: "opening_ceremony",
      label: "Opening Ceremony",
      type: "boolean",
      default: true,
      description: "Turn 0 includes a dramatic opening narration before player agency",
    },
    {
      key: "session_structure",
      label: "Session Structure",
      type: "select",
      options: [
        { value: "open_ended", label: "Open-ended (sandbox)" },
        { value: "chapter_based", label: "Chapter-based (act breaks)" },
        { value: "encounter_chain", label: "Encounter Chain (linear set pieces)" },
      ],
      default: "open_ended",
      description: "How the session is paced and structured",
    },
  ],
  systemPromptSection: (config) => {
    const structure = config.session_structure || "open_ended";
    const opening = config.opening_ceremony !== false;

    const structureDesc: Record<string, string> = {
      open_ended: "The session is a sandbox — players drive the story freely. No forced structure.",
      chapter_based: "Structure the session into chapters/acts. After major story beats, announce chapter transitions (e.g., '--- Chapter 2: The Gathering Storm ---'). Each chapter shifts tone or stakes. Track 'current_chapter' in state.",
      encounter_chain: "Progress through a linear sequence of set-piece encounters. Each encounter leads to the next. Track 'encounter_index' in state. Between encounters, allow brief rest/roleplay moments.",
    };

    const openingDesc = opening
      ? "\n- OPENING CEREMONY: On Turn 0, deliver a dramatic opening narration that establishes the scene, mood, and stakes BEFORE the player can act. Set the stage cinematically."
      : "\n- Skip opening ceremony — jump straight into player agency.";

    return `## GAME PHASES (System Rule)
- Session: ${structure}. ${structureDesc[structure]}${openingDesc}`;
  },
  tools: [],
  widgets: [],
  initializeState: (config) => ({
    _session_structure: config.session_structure || "open_ended",
    current_chapter: config.session_structure === "chapter_based" ? 1 : undefined,
    encounter_index: config.session_structure === "encounter_chain" ? 0 : undefined,
  }),
};
